// src/Component/ChatAdmin.js
import React, { useEffect, useState, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Send, Eye, Edit, Trash } from "lucide-react";
import { BASE_URL } from "../Apis/apiConfig";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function ChatAdmin() {
  const { member_name, title, flatNo } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatId, setChatId] = useState(null);
  const [viewPdfId, setViewPdfId] = useState(null);

  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const fetchChats = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const societyName = localStorage.getItem("society");

        const [userRes, adminRes] = await Promise.all([
          fetch(
            `${BASE_URL}/chat/get-all-chat-by-title?memberName=${encodeURIComponent(
              member_name
            )}&title=${encodeURIComponent(title)}&societyName=${encodeURIComponent(
              societyName
            )}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          ),
          fetch(
            `${BASE_URL}/chat/get-chat-by-memeber-and-title?memberName=${encodeURIComponent(
              member_name
            )}&title=${encodeURIComponent(title)}&societyName=${encodeURIComponent(
              societyName
            )}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        ]);

        if (!userRes.ok || !adminRes.ok) throw new Error("Failed to fetch");

        const userData = await userRes.json();
        const adminData = await adminRes.json();

        if (userData && userData.length > 0) {
          const lastItem = userData[userData.length - 1];
          const id = lastItem?.chatId || lastItem?.userChatId;
          setChatId(id);
        }

        const userMsgs = userData.map((msg) => ({
          ...msg,
          sender: "user",
          date: msg.date || msg.timestamp,
        }));

        const adminMsgs = adminData.map((msg) => ({
          ...msg,
          sender: "admin",
          date: msg.date || msg.timestamp,
        }));

        const merged = [...userMsgs, ...adminMsgs].sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );

        setMessages(merged);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, [title, member_name]);

  const handleSend = async () => {
    const token = localStorage.getItem("token");
    const societyName = localStorage.getItem("society");

    if (pdfFile) {
      const formData = new FormData();
      formData.append("file", pdfFile);
      formData.append("memberName", member_name);
      formData.append("title", title);
      formData.append("sender", "admin");
      formData.append("societyName", societyName);
      formData.append("message", newMessage)
      formData.append("flatNo", flatNo);
      if (chatId) formData.append("userChatId", chatId);

      const tempMsg = {
        sender: "admin",
        message: "",
        pdfFileName: pdfFile.name,
        date: new Date().toISOString(),
        _temp: true,
      };

      setMessages((prev) => [...prev, tempMsg]);

      try {
        const response = await fetch(`${BASE_URL}/chat/add-message`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (!response.ok) {
          throw new Error("Server error");
        }

        let savedMsg = null;

        try {
          savedMsg = await response.json();
        } catch {
          savedMsg = tempMsg;
        }

        toast.success("PDF uploaded successfully ✅");

        setMessages((prev) =>
          prev.map((msg) =>
            msg._temp && msg.pdfFileName === pdfFile.name
              ? { ...savedMsg, _temp: false }
              : msg
          )
        );

        setPdfFile(null);
      } catch (err) {
        console.error(err);
        toast.error("Error uploading PDF");
      }
      return;
    }

    if (!newMessage.trim()) return;

    const tempMsg = {
      sender: "admin",
      message: newMessage,
      date: new Date().toISOString(),
      _temp: true,
    };

    setMessages((prev) => [...prev, tempMsg]);

    try {
      await fetch(
        `${BASE_URL}/chat/add-message?title=${encodeURIComponent(
          title
        )}&message=${encodeURIComponent(
          newMessage
        )}&memberName=${encodeURIComponent(
          member_name
        )}&sender=admin&flatNo=${encodeURIComponent(flatNo)}${chatId ? `&userChatId=${chatId}` : ""
        }`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setNewMessage("");
    } catch (err) {
      toast.error("Error sending message");
    }
  };

  const parseDBDate = (date) => (date ? new Date(date) : new Date());

  const formatDate = (date) => {
    const d = parseDBDate(date);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (date) => {
    const d = parseDBDate(date);
    return d.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (loading) return <p className="text-center mt-4">Loading...</p>;
  if (error)
    return <p className="text-center mt-4 text-danger">Error: {error}</p>;

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: "#f4f7fb" }}>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          background: "linear-gradient(135deg, #087db7, #0aa2e3)",
          color: "white",
          padding: "12px 18px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: "500",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        <span>Subject: {title}</span>
        <span>Flat no: {flatNo}</span>
        <span>Member Name: {member_name}</span>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "20px", paddingTop: "140px" }}>
        {messages.map((msg, index) => {
          const prevSender = messages[index - 1]?.sender;
          const showHeader = msg.sender !== prevSender;

          return (
            <div key={index}>
              {showHeader && (
                <div style={{ textAlign: "center", margin: "15px 0", fontSize: "12px" }}>
                  <span
                    style={{
                      background: "#e3e8ef",
                      padding: "5px 14px",
                      borderRadius: "20px",
                      color: "#555",
                      fontWeight: "500",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    {msg.sender === "admin"
                      ? `You replied — ${formatDate(msg.date)}`
                      : `User message — ${formatDate(msg.date)}`}
                  </span>
                </div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ display: "flex", justifyContent: msg.sender === "admin" ? "flex-end" : "flex-start", marginBottom: "14px" }}
              >
                <div
                  style={{
                    background: msg.sender === "admin" ? "linear-gradient(135deg, #087db7, #0aa2e3)" : "#ffffff",
                    color: msg.sender === "admin" ? "#fff" : "#333",
                    padding: "12px 16px",
                    borderRadius: "18px",
                    maxWidth: "65%",
                    boxShadow: "0 3px 8px rgba(0,0,0,0.12)",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    transition: "all 0.2s ease",
                  }}
                >

                  {msg.pdfFileName ? (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span
                          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontWeight: "600" }}
                          onClick={() => setViewPdfId(viewPdfId === msg.admidChatId ? null : msg.admidChatId)}
                        >
                          <Eye size={16} /> {msg.pdfFileName}
                        </span>

                        {msg.sender === "admin" && (
                          <div style={{ marginLeft: "auto", display: "flex", gap: "6px" }}>

                            <button
                              onClick={async () => {
                                const fileInput = document.createElement("input");
                                fileInput.type = "file";
                                fileInput.accept = "application/pdf";
                                fileInput.click();

                                fileInput.onchange = async () => {
                                  const newFile = fileInput.files[0];
                                  if (!newFile) return;

                                  const formData = new FormData();
                                  formData.append("chatId", msg.admidChatId);
                                  formData.append("societyName", localStorage.getItem("society"));
                                  formData.append("pdf", newFile);


                                  try {
                                    const token = localStorage.getItem("token");
                                    const res = await fetch(`${BASE_URL}/chat/get-edit-by-admin`, {
                                      method: "POST",
                                      headers: {
                                        Authorization: `Bearer ${token}`, 
                                      },
                                      body: formData,
                                    });

                                    if (!res.ok) throw new Error("Failed to update message");

                                    const updatedMsg = await res.text();
                                    toast.success(updatedMsg);

                                  } catch (err) {
                                    toast.error(err.message || "Error updating PDF");
                                  }
                                };
                              }}
                              style={{
                                background: "#ffc107",
                                border: "none",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                cursor: "pointer",
                              }}
                            >
                              <Edit size={14} />
                            </button>


                            <button
                              onClick={async () => {
                                if (!window.confirm("Are you sure you want to delete this message?")) return;

                                try {
                                  const token = localStorage.getItem("token");
                                  const res = await fetch(`${BASE_URL}/chat/get-delete-by-admin?chatId=${msg.admidChatId}`, {
                                    method: "DELETE",
                                    headers: {
                                      Authorization: `Bearer ${token}`,
                                    },
                                  });

                                  if (!res.ok) throw new Error("Failed to delete message");

                                  const result = await res.text();
                                  toast.success(result);


                                  setMessages((prev) => prev.filter((m) => m.admidChatId !== msg.admidChatId));
                                } catch (err) {
                                  toast.error(err.message || "Error deleting message");
                                }
                              }}
                              style={{
                                background: "#dc3545",
                                border: "none",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                cursor: "pointer",
                                color: "white",
                              }}
                            >
                              <Trash size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                      {viewPdfId === msg.admidChatId && (
                        <iframe
                          src={`data:application/pdf;base64,${msg.pdfFile}`}
                          style={{ width: "100%", height: "400px", marginTop: "10px", borderRadius: "8px" }}
                          title={msg.pdfFileName}
                        />
                      )}
                    </div>
                  ) : (
                    <div style={{ whiteSpace: "pre-line" }}>{msg.message}</div>
                  )}

                  <div style={{ fontSize: "11px", marginTop: "6px", textAlign: "right", opacity: 0.75 }}>
                    {formatTime(msg.date)}
                  </div>
                </div>
              </motion.div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      {/* INPUT */}
      <div
        style={{
          padding: "12px",
          background: "#ffffff",
          borderTop: "1px solid #ddd",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => setPdfFile(e.target.files[0])}
          style={{ fontSize: "12px", border: "1px solid #ccc", borderRadius: "6px", padding: "4px" }}
        />

        <input
          type="text"
          className="form-control"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          style={{ borderRadius: "25px", padding: "10px 15px", border: "1px solid #ccc" }}
        />

        <button
          onClick={handleSend}
          style={{
            background: "linear-gradient(135deg, #087db7, #0aa2e3)",
            border: "none",
            color: "white",
            borderRadius: "50%",
            width: "42px",
            height: "42px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <Send size={18} />
        </button>
      </div>

      <ToastContainer />
    </div>
  );
}